import { ActivityType } from "encamp-prisma/generated/client";
import { prettyPrintEnumValue } from "../utils/prettyPrintEnumValue";

type ActivityDisplayConfig = {
  activityType: ActivityType;
  activityDescription: string;
  activityDisplayName: string | null;
};

/**
 * Get Customer Facing Activity Display Name
 */
export function getActivityDisplayName({
  state,
  activityType,
  activityDescription,
  username,
  cn,
}: {
  state: string;
  activityType: ActivityType;
  activityDescription: string;
  username?: string;
  cn?: string;
}) {
  if (activityType === ActivityType.Presubmission) {
    return "Verifying report data";
  }

  // Handle cohort activities with standard display names
  const cohortActivityName = getCohortActivityDisplayName(activityType);
  if (cohortActivityName) {
    return cohortActivityName;
  }

  const key = getActivityDisplayNameKey({
    state,
    activityType,
    activityDescription,
  });
  const displayConfig = activityDisplayMap.get(key);
  if (displayConfig) {
    if (displayConfig.activityDisplayName === null) {
      return activityDescription;
    }
    return interpolateDisplayName(displayConfig.activityDisplayName, {
      username,
      CN: cn,
    });
  }

  return getStaffFacingActivityTitle(
    {
      description: activityDescription,
      type: activityType,
    },
    username,
    cn
  );
}

function interpolateDisplayName(
  template: string,
  values: Record<string, string | undefined>
): string {
  return template.replace(/\[(\w+)]/g, (_, key) => {
    return values[key] ? `'${values[key]}'` : `[${key}]`; // fallback keeps placeholder if no value provided
  });
}

function getCohortActivityDisplayName(
  activityType: ActivityType
): string | null {
  switch (activityType) {
    case ActivityType.DistributeCohortDocuments:
      return "Distribute Cohort Documents";
    case ActivityType.ValidateCohort:
      return "Validate Cohort";
    case ActivityType.InitializeCohorts:
      return "Initialize Cohorts";
    case ActivityType.ValidateCohortMembership:
      return "Validate Cohort Membership";
    case ActivityType.WaitForCohorts:
      return "Wait for Cohorts";
    default:
      return null;
  }
}

export function getStaffFacingActivityTitle(
  activity: {
    description?: string | null;
    type: ActivityType;
  },
  username?: string,
  cn?: string
): string {
  const displayName = interpolateDisplayName(activity.description ?? "", {
    username,
    CN: cn,
  });
  return [convertEnumToHumanReadable(activity.type), displayName]
    .filter(Boolean)
    .join(" - ");
}

// New function to get activity titles without descriptions (except for Staff activities)
export function getActivityTitleOnly(
  activity: {
    description?: string | null;
    type: ActivityType;
  },
  username?: string,
  cn?: string
): string {
  // For Staff activities, keep the full title with description
  if (activity.type === ActivityType.Staff) {
    return getStaffFacingActivityTitle(activity, username, cn);
  }

  // For all other activities, only show the activity type
  return convertEnumToHumanReadable(activity.type);
}

function convertEnumToHumanReadable(value: string): string {
  // Special cases for abbreviations
  const specialCases: Partial<Record<ActivityType, string>> = {
    WaitForAllLEPC: "Wait For All LEPC Reports",
    WaitForAllFD: "Wait For All FD Reports",
  };

  if (value in specialCases) {
    return specialCases[value as ActivityType] ?? value;
  }

  return prettyPrintEnumValue(value);
}

function getActivityDisplayNameKey({
  state,
  activityType,
  activityDescription,
}: {
  state: string;
  activityType: ActivityType;
  activityDescription: string;
}) {
  return `${state}-${activityType}-${activityDescription}`;
}

/**
 * List of activity display names by state, activity name, and activity description
 */
const activityDisplayConfigsByState: {
  [state: string]: ActivityDisplayConfig[];
} = {
  AK: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  AL: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to fire department if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Birmingham Fire Department Questionaire",
      activityDisplayName:
        "Completing Birmingham Fire Department Questionnaire",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Morgan County LEPC Questionaire",
      activityDisplayName:
        "Completing Morgan County Local Emergency Planning Committee Questionnaire",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  AR: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Arkansas Tier2 Submit",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  AZ: [
    {
      activityType: ActivityType.CreateZip,
      activityDescription: "Bulk Mail FD",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Bulk Mail FD",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Bulk Mail FD",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "FD Bulk",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.CreateZip,
      activityDescription: "Bulk Mail LEPC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Bulk Mail LEPC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Bulk Mail LEPC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Arizona Portal",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.WaitForAllFD,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Fire District together",
    },
    {
      activityType: ActivityType.WaitForAllLEPC,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  CA: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "California Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  CO: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Colorado Tier2 Submit",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Colorado Tier2 Submit - Revision",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Colorado Springs Portal",
      activityDisplayName: "Submitting report to Colorado Springs portal",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  CT: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Connecticut Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.FillPdf,
      activityDescription: "Sign Report",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC",
      activityDisplayName:
        "Emailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "FD",
      activityDisplayName: "Emailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  DE: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Delaware Portal",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  FL: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Florida ePlan",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  GA: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.CreatePdf,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Hawaii Tier2 Submit",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  IA: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  ID: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "SERC",
      activityDisplayName:
        "Emailing report to State Emergency Response Commission if required",
    },
    {
      activityType: ActivityType.CreatePdf,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  IL: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Illinois Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.FillPdf,
      activityDescription: "Sign Report",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Chicago Portal",
      activityDisplayName: "Submitting report to Chicago portal",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  IN: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Indiana Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.WaitForAllCredentialReports,
      activityDescription: "",
      activityDisplayName:
        "Submitting fee payment for all reports under [username] credential",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Indiana Invoice - Once Per Credential",
      activityDisplayName: null,
    },
  ],
  KS: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Kansas Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.WaitForAllCredentialReports,
      activityDescription: "",
      activityDisplayName:
        "Submitting fee payment for all reports under [username] credential",
    },
    {
      activityType: ActivityType.WaitForAllLEPC,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Fire District together",
    },
    {
      activityType: ActivityType.WaitForAllFD,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Ford County LEPC",
      activityDisplayName:
        "Submitting report to Ford County Local Emergency Planning Committee",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Lenexa Fire Dept Portal",
      activityDisplayName: "Submitting report to Lenexa Fire Department portal",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Kansas Invoice - Once Per Credential",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Check",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.CreateZip,
      activityDescription: "Bulk Mail LEPC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Bulk Mail LEPC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Bulk Mail LEPC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC Bulk",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.CreateZip,
      activityDescription: "Bulk Mail FD",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Bulk Mail FD",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Bulk Mail FD",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "FD Bulk",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
  ],
  KY: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Kentucky Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.WaitForAllCredentialReports,
      activityDescription: "",
      activityDisplayName:
        "Submitting fee payment for all reports under [username] credential",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Kentucky Invoice - Once Per Credential",
      activityDisplayName: null,
    },
  ],
  LA: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.WaitForAllCredentialReports,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports under [username] credential and paying fees",
    },
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "downloadReport",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.WaitForAllLEPC,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Fire District together",
    },
    {
      activityType: ActivityType.WaitForAllFD,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "East Baton Rouge Portal",
      activityDisplayName: "Submitting report to East Baton Rouge portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Louisiana Invoice - Once Per Credential",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to SERC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  MA: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Massachusetts Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  MD: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Maryland Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.WaitForAllCredentialReports,
      activityDescription: "",
      activityDisplayName:
        "Submitting fee payment for all reports under [username] credential",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Howard County Portal",
      activityDisplayName: "Submitting report to Howard County portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Montgomery County Portal",
      activityDisplayName: "Submitting report to Montgomery County portal",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Maryland Invoice - Once Per Credential",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Check",
      activityDisplayName: null,
    },
  ],
  ME: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription:
        "Maine Certification Letter - Subject to Emergency Planning",
      activityDisplayName: "Preparing payment and documentation",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "SERC",
      activityDisplayName:
        "Emailing report to State Emergency Response Commission if required",
    },
    {
      activityType: ActivityType.CreatePdf,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Check",
      activityDisplayName: "Mailing check for fee payment to state if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  MI: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Michigan Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  MN: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Minnesota Portal",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "FD",
      activityDisplayName: "Emailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  MO: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Missouri Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC",
      activityDisplayName:
        "Emailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "FD",
      activityDisplayName: "Emailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  MS: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  MT: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.WaitForAllLEPC,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Fire District together",
    },
    {
      activityType: ActivityType.WaitForAllFD,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  NC: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "North Carolina ePlan",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.SignReportPages,
      activityDescription: "Sign Report PDF",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Alamance County Portal",
      activityDisplayName: "Submitting report to Alamance County portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Mecklenburg County LEPC",
      activityDisplayName:
        "Submitting report to Mecklenburg County Local Emergency Planning Committee",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  ND: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "North Dakota Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.WaitForAllCredentialReports,
      activityDescription: "",
      activityDisplayName:
        "Submitting fee payment for all reports under [username] credential",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "North Dakota Invoice - Once Per Credential",
      activityDisplayName: null,
    },
  ],
  NE: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Nebraska Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "New Hampshire Tier2 Submit",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.CreatePdf,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  NJ: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "New Jersey Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.FillPdf,
      activityDescription: "Sign Report",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to Lead Agency",
      activityDisplayName: "Mailing report to Lead Agency if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to PD",
      activityDisplayName: "Mailing report to PD if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "Lead Agency",
      activityDisplayName: "Emailing report to Lead Agency if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Howell Township Form and Fees",
      activityDisplayName: "Submitting Howell Township form and fees",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  NM: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "SERC",
      activityDisplayName:
        "Emailing report to State Emergency Response Commission if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC",
      activityDisplayName:
        "Emailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.WaitForAllStateReports,
      activityDescription: "",
      activityDisplayName:
        "Submitting payment for all reports in that jurisdiction",
    },

    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "New Mexico Cover Letter - Once Per Organization",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Check",
      activityDisplayName: null,
    },
  ],
  NV: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Nevada Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.WaitForAllCredentialReports,
      activityDescription: "",
      activityDisplayName:
        "Submitting fee payment for all reports under [username] credential",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Nevada Invoice - Once Per Credential",
      activityDisplayName: null,
    },
  ],
  NY: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.WaitForAllLEPC,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
    },
    {
      activityType: ActivityType.WaitForAllFD,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Fire District together",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "New York City Portal",
      activityDisplayName: "Submitting report to New York City portal",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  OH: [
    {
      activityType: ActivityType.CreateZip,
      activityDescription: "Bulk Mail FD",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Bulk Mail FD",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Bulk Mail FD",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "FD Bulk",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.CreateZip,
      activityDescription: "Bulk Mail LEPC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Bulk Mail LEPC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Bulk Mail LEPC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC Bulk",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "Batch",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.CreateZip,
      activityDescription: "Certification Letters",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "SERC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.AddDocument,
      activityDescription: "OH Certification Form",
      activityDisplayName: "Preparing Ohio Certification Form",
    },
    {
      activityType: ActivityType.FillPdf,
      activityDescription: "Fill Certification",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.CreatePdf,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.WaitForAllLEPC,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
    },
    {
      activityType: ActivityType.WaitForAllFD,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Fire District together",
    },
    {
      activityType: ActivityType.WaitForAllStateReports,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the state together",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Fee Worksheet",
      activityDisplayName: "Submitting fee payment",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Cleveland Fire Department",
      activityDisplayName: "Submitting report to Cleveland Fire Department",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Warren County Portal",
      activityDisplayName: "Submitting report to Warren County portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "City of Cleveland",
      activityDisplayName: "Submitting report to City of Cleveland",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  OK: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.WaitForAllCredentialReports,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports for this jurisdiction to the state in a single T2S file and paying fees",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "Batch",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Oklahoma Tier2 Submit - Once Per Credential",
      activityDisplayName: null,
    },
  ],
  OR: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Oregon Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  PA: [
    {
      activityType: ActivityType.CreateZip,
      activityDescription: "Bulk Mail FD",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Bulk Mail FD",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Bulk Mail FD",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.CreateZip,
      activityDescription: "Bulk Mail LEPC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Bulk Mail LEPC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Bulk Mail LEPC",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Pennsylvania Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Pennsylvania Portal - Revision",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.FillPdf,
      activityDescription: "Fill Certification",
      activityDisplayName: "Generating certification letter",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Check",
      activityDisplayName:
        "Mailing fee payment and certification letter to state if required",
    },
    {
      activityType: ActivityType.WaitForAllLEPC,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
    },
    {
      activityType: ActivityType.WaitForAllFD,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Fire District together",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Berks County Portal",
      activityDisplayName: "Submitting report to Berks County portal",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: "Filing complete",
    },
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Puerto Rico Tier2 Submit",
      activityDisplayName: "Submitting report to state portal",
    },
  ],
  PR: [
    {
      activityType: ActivityType.CreatePdf,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  RI: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "SERC",
      activityDisplayName:
        "Emailing report to State Emergency Response Commission if required",
    },
    {
      activityType: ActivityType.CreatePdf,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  SC: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Richland County Form and Fees",
      activityDisplayName: "Submitting Richland County Form and fees",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  SD: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "South Dakota Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  TN: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  TX: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.WaitForAllCNReports,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports under [CN] Customer Number to state portal and paying fees",
    },
    {
      activityType: ActivityType.CreatePdf,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Plano Fire-Rescue Portal",
      activityDisplayName: "Submitting report to Plano Fire-Rescue portal",
    },
    {
      activityType: ActivityType.WaitForAllLEPC,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
    },
    {
      activityType: ActivityType.WaitForAllFD,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Fire District together",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Texas Portal - Once Per CN",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
  ],
  UT: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Utah Tier2 Submit",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.CreatePdf,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.WaitForAllLEPC,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
    },
    {
      activityType: ActivityType.WaitForAllFD,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Fire District together",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Tooele County Portal",
      activityDisplayName: "Submitting report to Tooele County portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Utah County Portal",
      activityDisplayName: "Submitting report to Utah County portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Davis County Portal",
      activityDisplayName: "Submitting report to Davis County portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Salt Lake City Portal",
      activityDisplayName: "Submitting report to Salt Lake City portal",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  VA: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.AddDocument,
      activityDescription: "VA Certification Form",
      activityDisplayName: "Completing VA Certification form",
    },
    {
      activityType: ActivityType.FillPdf,
      activityDescription: "Fill Certification",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "SERC",
      activityDisplayName:
        "Emailing report and certification to State Emergency Response Commission",
    },
    {
      activityType: ActivityType.CreatePdf,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SignReportPages,
      activityDescription: "Sign Report PDF",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Caroline County LEPC Portal",
      activityDisplayName:
        "Submitting report to Caroline County Local Emergency Planning Committee portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "City of Fredericksburg LEPC Portal",
      activityDisplayName:
        "Submitting report to City of Fredericksburg Local Emergency Planning Committee portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "City of Richmond Portal",
      activityDisplayName: "Submitting report to City of Richmond portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Fairfax County Portal",
      activityDisplayName: "Submitting report to Fairfax County portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Henrico County Portal",
      activityDisplayName: "Submitting report to Henrico County portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "King George County LEPC Portal",
      activityDisplayName:
        "Submitting report to King George County Local Emergency Planning Committee portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Roanoke Valley Joint LEPC Portal",
      activityDisplayName:
        "Submitting report to Roanoke Valley Joint Local Emergency Planning Committee portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Spotsylvania County LEPC Portal",
      activityDisplayName:
        "Submitting report to Spotsylvania County Local Emergency Planning Committee portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Stafford County LEPC Portal",
      activityDisplayName:
        "Submitting report to Stafford County Local Emergency Planning Committee portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "City of Lynchburg Portal",
      activityDisplayName: "Submitting report to City of Lynchburg portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Mecklenburg County LEPC Portal",
      activityDisplayName:
        "Submitting report to Mecklenburg County Local Emergency Planning Committee portal",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Newport News Fire Department Portal",
      activityDisplayName:
        "Submitting report to Newport News Fire Department portal",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  VT: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Vermont Tier2 Submit",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.CreatePdf,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to LEPC",
      activityDisplayName:
        "Mailing report to Local Emergency Planning Committee if required",
    },
    {
      activityType: ActivityType.Lob,
      activityDescription: "Mail to FD",
      activityDisplayName: "Mailing report to Fire District if required",
    },
    {
      activityType: ActivityType.SendEmail,
      activityDescription: "LEPC/FD",
      activityDisplayName: "Emailing report to local agencies if required",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  WA: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Washington Portal",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.WaitForAllLEPC,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Local Emergency Planning Committee together",
    },
    {
      activityType: ActivityType.WaitForAllFD,
      activityDescription: "",
      activityDisplayName:
        "Submitting all reports in this jurisdiction to the Fire District together",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  WI: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Wisconsin Portal",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
  WV: [
    {
      activityType: ActivityType.Geppetto,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "West Virginia Portal",
      activityDisplayName: "Submitting report to state portal and paying fee",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "West Virginia Portal - Oil and Gas",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.WaitForAllCredentialReports,
      activityDescription: "",
      activityDisplayName:
        "Submitting fee payment for all reports under [username] credential",
    },
    {
      activityType: ActivityType.CollectEvidence,
      activityDescription: "Collect evidence: collectEvidenceOfCompletion",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "West Virginia Invoice - Once Per Credential",
      activityDisplayName: null,
    },
  ],
  WY: [
    {
      activityType: ActivityType.Tier2Submit,
      activityDescription: "",
      activityDisplayName: "Generating T2S report",
    },
    {
      activityType: ActivityType.Tier2SubmitValidation,
      activityDescription: "",
      activityDisplayName: null,
    },
    {
      activityType: ActivityType.Staff,
      activityDescription: "Wyoming Tier2 Submit",
      activityDisplayName: "Submitting report to state portal",
    },
    {
      activityType: ActivityType.SendSupplementalAgencyCommunication,
      activityDescription: "",
      activityDisplayName: "Send report to local agencies if required",
    },
    {
      activityType: ActivityType.GenerateCustomerDeliverables,
      activityDescription: "",
      activityDisplayName: "Filing complete",
    },
  ],
};

/**
 * Map of activity display names by state, activity name, and activity description
 */
const activityDisplayMap = new Map(
  Object.entries(activityDisplayConfigsByState).flatMap(([state, activities]) =>
    activities.map((activity) => [
      getActivityDisplayNameKey({
        state,
        activityType: activity.activityType,
        activityDescription: activity.activityDescription,
      }),
      activity,
    ])
  )
);
